import React, { useEffect, useRef, useState } from 'react';

const EditableName = ({
    value,
    onSave,
    className = '',
    inputClassName = '',
    placeholder = 'Untitled',
    title = 'Click to rename',
    activateOn = 'click', // 'click' | 'doubleClick'
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => { setDraft(value || ''); }, [value]);

    useEffect(() => {
        if (!editing) return;
        const id = requestAnimationFrame(() => {
            inputRef.current?.focus();
            inputRef.current?.select();
        });
        return () => cancelAnimationFrame(id);
    }, [editing]);

    const commit = async () => {
        const trimmed = (draft || '').trim();
        if (!trimmed || trimmed === value) {
            setDraft(value || '');
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await onSave(trimmed);
            setEditing(false);
        } catch {
            setDraft(value || '');
            setEditing(false);
        } finally {
            setSaving(false);
        }
    };

    const cancel = () => {
        setDraft(value || '');
        setEditing(false);
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                className={`editable-input ${className} ${inputClassName}`}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commit(); }
                    else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
                }}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                disabled={saving}
                placeholder={placeholder}
            />
        );
    }

    // doubleClick mode leaves single clicks alone so the surrounding row
    // can still handle selection; only the double-click opens the editor.
    const activate = (e) => { e.stopPropagation(); setEditing(true); };

    return (
        <span
            className={`editable-name ${className}`}
            title={title}
            onClick={activateOn === 'doubleClick' ? undefined : activate}
            onDoubleClick={activateOn === 'doubleClick' ? activate : undefined}
        >
            {value || placeholder}
        </span>
    );
};

export default EditableName;
